import { useCurrentUser } from '@bonusly/who-am-i';

import { useBizyUnreadNotifications } from '../../../queries/bizy-notifications';
import { useFetchNotifications } from '../../../queries/notifications';
import { useBizyPeopleEntries } from './use-bizy-people-entries';
import { usePeopleBrowserEnabled } from './use-people-browser-enabled';

/**
 * The Bizy unread signal for the navbar dot/badge and the browser-tab count.
 *
 * When the People Browser is enabled, the source of truth is the `unread`
 * property of the (up-to-5) surfaced people — so viewing/marking a person seen
 * clears the dot and decrements the count immediately (BIZY-636/637). Otherwise
 * we fall back to the `/bizy/notifications/unread` endpoint (proactive
 * conversations, legacy behavior).
 *
 * Note: this intentionally does NOT change `useHasBizyNotification` /
 * `useUnreadBizy*` consumers used by the proactive-conversation open/peek path,
 * which still key off the notifications endpoint.
 */
function useBizyUnreadSource(): { unreadCount: number; hasUnread: boolean } {
	const peopleBrowserEnabled = usePeopleBrowserEnabled();
	const { unreadEntries } = useBizyPeopleEntries(peopleBrowserEnabled);
	const { data } = useBizyUnreadNotifications();

	if (peopleBrowserEnabled) {
		// Count distinct unread *people*, not moments — a teammate can have
		// several unread moments, matching the greeter's deduped "N people".
		// The backend already returns one entry per person; this stays as a guard.
		const uniqueUnreadPeople = new Set(unreadEntries.map((entry) => entry.person.id)).size;
		return { unreadCount: uniqueUnreadPeople, hasUnread: uniqueUnreadPeople > 0 };
	}
	return { unreadCount: data?.unreadCount ?? 0, hasUnread: data?.hasUnread ?? false };
}

/**
 * Returns whether the current user has an unseen Bizy notification.
 *
 * Drives the waving state + dot on the navbar Ask Bizy button. Backed by a
 * real React Query against `/api/v2/bizy/notifications/unread` that
 * refetches on window focus so a notification delivered while the user is
 * on the page surfaces without a hard reload. The indicator goes away when
 * `useBizyMarkNotificationsSeenMutation` invalidates the query.
 */
export function useHasBizyNotification(): boolean {
	const { data } = useBizyUnreadNotifications();
	return data?.hasUnread ?? false;
}

/**
 * Returns the count of unread proactive Bizy notifications. Drives the
 * numeric badge on the navbar Ask Bizy button (matches the announcement
 * counter pattern). Always returns 0 when Bizy is disabled or the query
 * hasn't resolved yet — never undefined.
 */
export function useBizyUnreadCount(): number {
	const { unreadCount } = useBizyUnreadSource();
	return unreadCount;
}

export function useBizyNotificationAttention(): { shouldSurface: boolean; unreadCount: number } {
	const { unreadCount, hasUnread } = useBizyUnreadSource();
	const user = useCurrentUser();
	const { unreadReceivedRecognitionCount, isSuccess } = useFetchNotifications(user.locale);
	const hasUnreadReceivedRecognition = unreadReceivedRecognitionCount > 0;
	const shouldSurface = hasUnread && isSuccess && !hasUnreadReceivedRecognition;
	return {
		shouldSurface,
		unreadCount: shouldSurface ? unreadCount : 0,
	};
}

/**
 * Returns the conversation id of the most-recent unread proactive notification,
 * if any. The chat panel uses this to jump straight to Bizy's opening message
 * when the panel is opened with an unread notification.
 *
 * Returns null when:
 *  - There are no unread notifications.
 *  - The unread notification has no linked ChatHistory (rare edge case;
 *    earlier proactive entries pre-`chat_history_id` may not have one).
 */
export function useUnreadBizyConversationId(): string | null {
	const { data } = useBizyUnreadNotifications();
	return data?.latestConversationId ?? null;
}

/**
 * Whether the most-recent unread notification is a People Browser observed
 * moment (which has no conversation to open) rather than a proactive
 * conversation.
 *
 * Defaults to `false` when the backend omits `latestUnreadKind` (pre-BIZY-633),
 * so the legacy conversation-peek behavior is preserved until the contract
 * lands.
 */
export function useIsUnreadBizyObservedMoment(): boolean {
	const { data } = useBizyUnreadNotifications();
	return data?.latestUnreadKind === 'observed_moment';
}

/**
 * The person id the most-recent unread observed-moment notification is about,
 * used to focus that entry in the People Browser when opened from the peek.
 * Null when the latest unread is a conversation or nothing is unread.
 */
export function useUnreadBizyPersonId(): string | null {
	const { data } = useBizyUnreadNotifications();
	return data?.latestUnreadPersonId ?? null;
}
